import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import JobTag from '../jobs/JobTag';

const BRAND_AVATAR_COLORS = ['#1BB4E0', '#0078C3', '#E67E22', '#8E44AD', '#27AE60', '#E74C3C'];

function currencySymbol(code) {
  if (code === 'USD') return '$';
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return `${code} `;
}

function formatSalary(min, max, currency, period) {
  const sym = currencySymbol(currency);
  if (period === 'yearly' || period === 'annual') {
    const a = Math.round(Number(min) / 1000);
    const b = Math.round(Number(max) / 1000);
    return `${sym}${a}k–${sym}${b}k/yr`;
  }
  if (period === 'hourly') {
    return `${sym}${min}–${sym}${max}/hr`;
  }
  if (period === 'monthly') {
    const a = Math.round(Number(min) / 1000);
    const b = Math.round(Number(max) / 1000);
    return `${sym}${a}k–${sym}${b}k/mo`;
  }
  if (period === 'project') {
    return `${sym}${min}–${sym}${max}`;
  }
  return `${sym}${min}–${sym}${max}`;
}

function timeAgo(isoString) {
  if (!isoString) return '';
  const then = new Date(isoString);
  const now = new Date();
  const diffMs = now - then;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Today';
  if (days === 1) return '1 day ago';
  if (days >= 2 && days <= 6) return `${days} days ago`;
  if (days >= 7 && days <= 13) return '1 week ago';
  const weeks = Math.floor(days / 7);
  if (days >= 14 && days <= 29) return `${weeks} weeks ago`;
  return '1 month ago';
}

function avatarColor(name) {
  const ch = (name && name.trim()[0]) || 'A';
  return BRAND_AVATAR_COLORS[ch.charCodeAt(0) % 6];
}

function resolveLogoUrl(logoPath, logoUrl, apiBase) {
  if (logoUrl) return logoUrl;
  if (!logoPath) return null;
  if (logoPath.startsWith('http')) return logoPath;
  const b = apiBase.replace(/\/$/, '');
  const p = logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
  return `${b}${p}`;
}

function formatEmploymentLabel(value) {
  if (!value) return '';
  return String(value)
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

function formatSeniorityTags(levels) {
  if (!levels || !Array.isArray(levels) || levels.length === 0) return null;
  const labels = {
    entry: 'Entry Level',
    mid: 'Mid-Level',
    senior: 'Senior',
    lead: 'Lead / Principal',
  };
  return levels.map((l) => labels[l] || l).join(' / ');
}

function RecentJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const apiBase = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  const fetchUrl = apiBase ? `${apiBase}/api/v1/jobs/recent` : '/api/v1/jobs/recent';

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(fetchUrl, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('bad status');
        const json = await res.json();
        if (!cancelled) {
          setJobs(Array.isArray(json.data) ? json.data : []);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setJobs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchUrl]);

  const linkStyle = { color: '#0078C3' };
  const cardBaseStyle = {
    background: '#FFFFFF',
    border: '1px solid #E1E4E8',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };

  const skeletonPulse = {
    animation: 'rwm-recent-jobs-pulse 1.5s ease-in-out infinite',
    background: '#E1E4E8',
    borderRadius: '6px',
  };

  return (
    <section
      className="bg-white py-5"
      data-testid="rwm-recent-jobs"
    >
      <style>
        {`
          @keyframes rwm-recent-jobs-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
      <div className="container">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <h2 className="fw-bold mb-0 text-start" style={{ color: '#0D1B2A' }}>
            Latest Remote Jobs
          </h2>
          <Link to="/jobs" className="text-decoration-none small fw-medium" style={linkStyle}>
            View all jobs →
          </Link>
        </div>

        {loading && (
          <div>
            {[1, 2, 3, 4, 5].map((k) => (
              <div
                key={k}
                className="d-flex align-items-center gap-3"
                data-testid="rwm-skeleton-card"
                style={{ ...cardBaseStyle, cursor: 'default' }}
              >
                <div style={{ width: 36, height: 36, ...skeletonPulse }} />
                <div className="flex-grow-1">
                  <div className="mb-2" style={{ height: 14, width: '70%', ...skeletonPulse }} />
                  <div style={{ height: 10, width: '45%', ...skeletonPulse }} />
                </div>
                <div style={{ width: 80, height: 36, ...skeletonPulse }} />
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <p className="text-muted small mb-0">Could not load jobs. Please try again later.</p>
        )}

        {!loading && !error && (
          <div>
            {jobs.map((job) => {
              const featured = Boolean(job.is_featured);
              const company = job.company || {};
              const category = job.category || {};
              const logoUrl = resolveLogoUrl(company.logo_path, company.logo_url, apiBase);
              const initial = (company.name && company.name.trim()[0]) || '?';
              const salaryText = formatSalary(
                job.salary_min,
                job.salary_max,
                job.salary_currency,
                job.salary_period
              );
              const seniorityTagText = formatSeniorityTags(job.seniority_level);
              const seniorityLevels = Array.isArray(job.seniority_level) ? job.seniority_level : [];
              const cardStyle = featured ? cardBaseStyle : cardBaseStyle;

              return (
                <div
                  key={job.id}
                  className="rwm-job-card-link"
                  data-testid={`rwm-job-card-link-${job.id}`}
                  onClick={() => navigate(`/jobs/${job.slug}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/jobs/${job.slug}`);
                    }
                  }}
                  role="link"
                  tabIndex={0}
                >
                <div
                  data-testid="rwm-job-card"
                  className={`d-flex align-items-center gap-3${featured ? ' rwm-recent-jobs__card--featured' : ''}`}
                  style={cardStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1BB4E0';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(27,180,224,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = featured ? '#F59E0B' : '#E1E4E8';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="flex-shrink-0" style={{ width: 36, height: 36 }}>
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={company.name || 'Company'}
                        width={36}
                        height={36}
                        className="rounded"
                        style={{ objectFit: 'cover', width: 36, height: 36 }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white"
                        style={{
                          width: 36,
                          height: 36,
                          backgroundColor: avatarColor(company.name || ''),
                          fontWeight: 600,
                          fontSize: 15,
                        }}
                      >
                        {initial.toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="flex-grow-1 min-w-0">
                    <div className="mb-1">
                      <Link
                        to={`/jobs/${job.slug}`}
                        className="text-decoration-none"
                        style={{ fontWeight: 500, color: '#0D1B2A', fontSize: 15 }}
                        data-testid="rwm-job-title-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {job.title}
                      </Link>
                      <span style={{ color: '#64748B', fontSize: 13 }}>{' · '}{company.name}</span>
                    </div>
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                      {category.name && category.id ? (
                        <JobTag
                          type="category"
                          value={category.id}
                          className="badge rounded-pill small rwm-job-type-badge"
                        >
                          {category.name}
                        </JobTag>
                      ) : category.name ? (
                        <span className="badge rounded-pill small rwm-job-type-badge">
                          {category.name}
                        </span>
                      ) : null}
                      {job.employment_type ? (
                        <JobTag
                          type="employment"
                          value={job.employment_type}
                          className="badge rounded-pill small"
                          style={{ background: '#F1F5F9', color: '#475569' }}
                        >
                          {formatEmploymentLabel(job.employment_type)}
                        </JobTag>
                      ) : null}
                      {job.location_country ? (
                        <JobTag
                          type="country"
                          value={job.location_country}
                          className="badge rounded-pill small"
                          style={{ background: '#F1F5F9', color: '#475569' }}
                        >
                          {job.location_country}
                        </JobTag>
                      ) : job.location_eligibility != null && job.location_eligibility !== '' ? (
                        <JobTag
                          type="location"
                          value={job.location_eligibility}
                          className="badge rounded-pill small"
                          style={{ background: '#F1F5F9', color: '#475569' }}
                        >
                          {job.location_eligibility}
                        </JobTag>
                      ) : null}
                      {seniorityTagText && seniorityLevels.length > 0 ? (
                        <JobTag
                          type="seniority"
                          value={seniorityLevels.join(',')}
                          className="badge rounded-pill small"
                          style={{ background: '#F1F5F9', color: '#475569' }}
                        >
                          {seniorityTagText}
                        </JobTag>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-end d-flex flex-column align-items-end">
                    {featured && (
                      <div
                        className="badge rounded-pill mb-1 rwm-featured-badge"
                        data-testid="rwm-featured-badge"
                      >
                        ★ Featured
                      </div>
                    )}
                    <div className="badge rounded-pill rwm-salary-badge" data-testid="rwm-salary-badge">
                      {salaryText}
                    </div>
                    <div className="mt-1" style={{ color: '#64748B', fontSize: 12 }}>
                      {timeAgo(job.approved_at)}
                    </div>
                    <Button
                      size="sm"
                      className="mt-2 text-white"
                      style={{ background: '#0078C3', borderColor: '#0078C3' }}
                      data-testid="rwm-job-apply"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/jobs/${job.slug}`);
                      }}
                    >
                      Apply Now
                    </Button>
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && (
          <div className="text-center mt-4">
            <Link
              to="/jobs"
              className="btn text-white px-4 py-2 rounded text-decoration-none d-inline-block"
              style={{ background: '#0078C3', borderColor: '#0078C3' }}
            >
              Browse All Remote Jobs
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default RecentJobs;
